import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '../store/authStore'
import { LEAST_PRIVILEGED_ROLE, ROLE_LABELS, ROLE_DESCRIPTIONS, roleBadgeClass } from '../utils/roles'
import {
  PlusIcon,
  UserIcon,
  EnvelopeIcon,
  TrashIcon,
  PencilSquareIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  CheckCircleIcon,
  ClipboardDocumentIcon,
  ClipboardDocumentCheckIcon,
  ArrowPathIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline'
import { resetUserMFA } from '../services/api'
import { errorText } from '../utils/errorText'

// API functions
const fetchUsers = async (token: string) => {
  const response = await fetch('/api/users', {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!response.ok) throw new Error('Failed to fetch users')
  const data = await response.json()
  // API returns {users: [...], total: N}, extract just the users array
  return data.users || []
}

const fetchInvitations = async (token: string) => {
  const response = await fetch('/api/users/invitations', {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!response.ok) throw new Error('Failed to fetch invitations')
  return response.json()
}

const fetchRoles = async (token: string) => {
  const response = await fetch('/api/users/roles', {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!response.ok) throw new Error('Failed to fetch roles')
  return response.json()
}

type SignInMethod = 'password' | 'sso'

interface TenantSSO {
  configured: boolean
  config?: {
    is_enabled: boolean
    enforcement_mode: 'optional' | 'required'
    display_name: string | null
    protocol: string
  }
}

const fetchTenantSSO = async (token: string): Promise<TenantSSO> => {
  const response = await fetch('/api/sso/config', {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!response.ok) throw new Error('Failed to fetch SSO configuration')
  return response.json()
}

const createInvitation = async ({
  token,
  email,
  role,
  message,
  sign_in_method,
}: {
  token: string
  email: string
  role: string
  message?: string
  sign_in_method: SignInMethod
}) => {
  const response = await fetch('/api/users/invitations', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, role, message, sign_in_method }),
  })
  if (!response.ok) {
    let errorMessage = 'Failed to send invitation'
    try {
      const error = await response.json()
      if (typeof error.detail === 'string') {
        errorMessage = error.detail
      } else if (Array.isArray(error.detail) && error.detail.length > 0) {
        // FastAPI validation errors: [{loc: [...], msg: "...", type: "..."}]
        errorMessage = error.detail.map((e: { msg?: string; message?: string }) => e.msg || e.message).filter(Boolean).join('; ') || 'Invalid input'
      }
    } catch {
      errorMessage = `Server error (${response.status})`
    }
    throw new Error(errorMessage)
  }
  return response.json()
}

const revokeInvitation = async ({
  token,
  invitationId,
}: {
  token: string
  invitationId: string
}) => {
  const response = await fetch(`/api/users/invitations/${invitationId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!response.ok) {
    let errorMessage = 'Failed to revoke invitation'
    try {
      const error = await response.json()
      if (typeof error.detail === 'string') {
        errorMessage = error.detail
      } else if (Array.isArray(error.detail) && error.detail.length > 0) {
        errorMessage = error.detail.map((e: { msg?: string; message?: string }) => e.msg || e.message).filter(Boolean).join('; ') || 'Invalid input'
      }
    } catch {
      errorMessage = `Server error (${response.status})`
    }
    throw new Error(errorMessage)
  }
  return response.json()
}

const removeUser = async ({
  token,
  userId,
}: {
  token: string
  userId: string
}) => {
  const response = await fetch(`/api/users/${userId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!response.ok) {
    let errorMessage = 'Failed to remove user'
    try {
      const error = await response.json()
      if (typeof error.detail === 'string') {
        errorMessage = error.detail
      } else if (Array.isArray(error.detail) && error.detail.length > 0) {
        errorMessage = error.detail.map((e: { msg?: string; message?: string }) => e.msg || e.message).filter(Boolean).join('; ') || 'Invalid input'
      }
    } catch {
      errorMessage = `Server error (${response.status})`
    }
    throw new Error(errorMessage)
  }
}

const updateUserRole = async ({
  token,
  userId,
  role,
}: {
  token: string
  userId: string
  role: string
}) => {
  const response = await fetch(`/api/users/${userId}`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ role }),
  })
  if (!response.ok) {
    let errorMessage = 'Failed to change role'
    try {
      const error = await response.json()
      if (typeof error.detail === 'string') {
        errorMessage = error.detail
      } else if (Array.isArray(error.detail) && error.detail.length > 0) {
        errorMessage = error.detail.map((e: { msg?: string; message?: string }) => e.msg || e.message).filter(Boolean).join('; ') || 'Invalid input'
      }
    } catch {
      errorMessage = `Server error (${response.status})`
    }
    throw new Error(errorMessage)
  }
  return response.json()
}

const resendInvitation = async ({
  token,
  invitationId,
}: {
  token: string
  invitationId: string
}) => {
  const response = await fetch(`/api/users/invitations/${invitationId}/resend`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!response.ok) {
    let errorMessage = 'Failed to resend invitation'
    try {
      const error = await response.json()
      if (typeof error.detail === 'string') {
        errorMessage = error.detail
      } else if (Array.isArray(error.detail) && error.detail.length > 0) {
        errorMessage = error.detail.map((e: { msg?: string; message?: string }) => e.msg || e.message).filter(Boolean).join('; ') || 'Invalid input'
      }
    } catch {
      errorMessage = `Server error (${response.status})`
    }
    throw new Error(errorMessage)
  }
  return response.json()
}

interface User {
  id: string
  username: string
  email: string
  full_name: string
  role: string
  is_active: boolean
  sso_provider: string | null
  created_at: string
  last_login: string | null
  /** Whether an authenticator app is enrolled. Always false for an SSO account. */
  mfa_enabled?: boolean
}

interface Invitation {
  id: string
  email: string
  role: string
  sign_in_method?: SignInMethod
  status: string
  expires_at: string
  created_at: string
  invited_by: {
    id: string
    username: string
    email: string
  } | null
  invite_link?: string  // Returned when creating invitation
}

interface Role {
  value: string
  label: string
  description: string
}

const UsersSettings: React.FC = () => {
  const { token, user: currentUser, canManageSubscription } = useAuthStore()
  const queryClient = useQueryClient()
  // Mirrors the backend rule that only an owner touches owner roles; the server refuses such a
  // request from anyone else, so no one else is shown a control that can only fail.
  const actingIsOwner = canManageSubscription()
  const [activeTab, setActiveTab] = useState<'users' | 'invitations'>('users')
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false)
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false)
  // The member whose two-factor authentication is about to be reset, while the confirmation is up.
  const [mfaResetUser, setMfaResetUser] = useState<User | null>(null)
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [selectedInvitation, setSelectedInvitation] = useState<Invitation | null>(null)
  // Opens on the least authority the product has. Granting console access is the highest
  // blast-radius choice here, and the cost of the default being wrong is paid in an incident
  // rather than in the UI, so the wrong default has to be the harmless one.
  const [inviteForm, setInviteForm] = useState<{ email: string; role: string; message: string; sign_in_method: SignInMethod }>(
    { email: '', role: LEAST_PRIVILEGED_ROLE, message: '', sign_in_method: 'sso' })
  const [roleForm, setRoleForm] = useState<string>(LEAST_PRIVILEGED_ROLE)
  const [error, setError] = useState<string | null>(null)
  const [inviteSuccessModal, setInviteSuccessModal] = useState<{ email: string; link: string } | null>(null)
  const [copiedLink, setCopiedLink] = useState(false)
  const [copiedInvitationId, setCopiedInvitationId] = useState<string | null>(null)

  // Fetch users
  const { data: users, isLoading: usersLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => fetchUsers(token!),
    enabled: !!token,
  })

  // Named wherever the owner role is out of reach, so a non-owner knows whom to ask.
  const owners: User[] = (users ?? []).filter((u: User) => u.role === 'owner')
  const ownerNames = owners.map((u) => u.full_name || u.email).join(', ')

  // Fetch invitations
  const { data: invitations, isLoading: invitationsLoading } = useQuery({
    queryKey: ['invitations'],
    queryFn: () => fetchInvitations(token!),
    enabled: !!token,
  })

  // Fetch roles
  const { data: roles } = useQuery<Role[]>({
    queryKey: ['roles'],
    queryFn: () => fetchRoles(token!),
    enabled: !!token,
  })

  // The tenant's SSO decides which sign-in an invitation may carry: none enabled -> password
  // only; required -> SSO only; optional -> the inviter chooses, SSO first.
  const { data: tenantSSO } = useQuery<TenantSSO>({
    queryKey: ['sso-config'],
    queryFn: () => fetchTenantSSO(token!),
    enabled: !!token,
  })
  const ssoEnabled = !!tenantSSO?.configured && !!tenantSSO.config?.is_enabled
  const ssoRequired = ssoEnabled && tenantSSO?.config?.enforcement_mode === 'required'
  const ssoLabel = tenantSSO?.config?.display_name || tenantSSO?.config?.protocol?.toUpperCase() || 'SSO'
  const inviteSignInMethod: SignInMethod =
    !ssoEnabled ? 'password' : ssoRequired ? 'sso' : inviteForm.sign_in_method

  // Create invitation mutation
  const inviteMutation = useMutation({
    mutationFn: createInvitation,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] })
      setIsInviteModalOpen(false)
      const email = inviteForm.email
      setInviteForm({ email: '', role: LEAST_PRIVILEGED_ROLE, message: '', sign_in_method: 'sso' })
      setError(null)
      // Show success modal with invite link if available
      if (data.invite_link) {
        setInviteSuccessModal({ email, link: data.invite_link })
        setCopiedLink(false)
      }
    },
    onError: (err: Error) => {
      setError(err.message)
    },
  })

  // Revoke invitation mutation
  const revokeMutation = useMutation({
    mutationFn: revokeInvitation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] })
      setSelectedInvitation(null)
    },
    onError: (err: Error) => {
      setError(err.message)
    },
  })

  // Resend invitation mutation
  const resendMutation = useMutation({
    mutationFn: resendInvitation,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] })
      // Show success modal with new invite link
      if (data.invite_link) {
        setInviteSuccessModal({ email: data.email, link: data.invite_link })
        setCopiedLink(false)
      }
    },
    onError: (err: Error) => {
      setError(err.message)
    },
  })

  // Remove user mutation
  const removeMutation = useMutation({
    mutationFn: removeUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setIsRemoveModalOpen(false)
      setSelectedUser(null)
    },
    onError: (err: Error) => {
      setError(err.message)
    },
  })

  // Change role mutation
  const roleMutation = useMutation({
    mutationFn: updateUserRole,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setIsRoleModalOpen(false)
      setSelectedUser(null)
      setError(null)
    },
    onError: (err: Error) => {
      setError(err.message)
    },
  })

  // Reset a member's two-factor authentication: clears it and signs them out everywhere.
  const mfaResetMutation = useMutation({
    mutationFn: (userId: string) => resetUserMFA(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setMfaResetUser(null)
    },
    onError: (err: unknown) => setError(errorText(err, 'Could not reset two-factor authentication.')),
  })

  const handleInvite = () => {
    if (!inviteForm.email.trim()) {
      setError('Please enter an email address')
      return
    }
    inviteMutation.mutate({
      token: token!,
      email: inviteForm.email.trim(),
      role: inviteForm.role,
      message: inviteForm.message || undefined,
      sign_in_method: inviteSignInMethod,
    })
  }

  const handleRemoveUser = () => {
    if (selectedUser) {
      removeMutation.mutate({ token: token!, userId: selectedUser.id })
    }
  }

  const handleChangeRole = () => {
    if (selectedUser && roleForm !== selectedUser.role) {
      roleMutation.mutate({ token: token!, userId: selectedUser.id, role: roleForm })
    }
  }

  const handleCopyInviteLink = async () => {
    if (inviteSuccessModal?.link) {
      try {
        await navigator.clipboard.writeText(inviteSuccessModal.link)
        setCopiedLink(true)
        setTimeout(() => setCopiedLink(false), 2000)
      } catch {
        // Fallback for older browsers
        const textArea = document.createElement('textarea')
        textArea.value = inviteSuccessModal.link
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        setCopiedLink(true)
        setTimeout(() => setCopiedLink(false), 2000)
      }
    }
  }

  const formatRole = (role: string) => ROLE_LABELS[role] || role

  const formatDate = (dateStr: string) => {
    return new Date(dateStr.endsWith('Z') ? dateStr : dateStr + 'Z').toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const formatSSOProvider = (provider: string) => {
    // provider format is "oidc:azure_ad" or "saml:okta" etc.
    const labels: Record<string, string> = {
      azure_ad: 'Azure AD',
      google: 'Google',
      okta: 'Okta',
      generic: 'SSO',
    }
    const parts = provider.split(':')
    const protocol = parts[0]?.toUpperCase() || ''
    const idp = parts[1] || ''
    const idpLabel = labels[idp] || idp
    return idpLabel ? `${idpLabel} (${protocol})` : protocol
  }

  const pendingInvitations = invitations?.filter((inv: Invitation) => inv.status === 'pending') || []

  return (
    <div className="p-8">
      <div className="space-y-6">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold text-theme-text-primary mb-2">Users & Permissions</h2>
            <p className="text-theme-text-secondary">
              Manage user access and permissions for your organization.
            </p>
          </div>
          <button
            onClick={() => setIsInviteModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            <PlusIcon className="w-5 h-5" />
            Invite User
          </button>
        </div>

      {/* Tabs */}
      <div className="flex border-b border-theme-border-primary">
        <button
          onClick={() => setActiveTab('users')}
          className={`px-6 py-3 text-sm font-medium transition-colors ${
            activeTab === 'users'
              ? 'text-blue-500 border-b-2 border-blue-500'
              : 'text-theme-text-tertiary hover:text-theme-text-secondary'
          }`}
        >
          Users
          {users && (
            <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-theme-tertiary text-theme-text-secondary">
              {users.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('invitations')}
          className={`px-6 py-3 text-sm font-medium transition-colors ${
            activeTab === 'invitations'
              ? 'text-blue-500 border-b-2 border-blue-500'
              : 'text-theme-text-tertiary hover:text-theme-text-secondary'
          }`}
        >
          Pending Invitations
          {pendingInvitations.length > 0 && (
            <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400">
              {pendingInvitations.length}
            </span>
          )}
        </button>
      </div>

      {activeTab === 'users' && users && (
        <p className="mb-3 text-sm text-theme-text-secondary" data-testid="tenant-owners">
          {owners.length > 0 ? (
            <>
              <span className="font-medium text-theme-text-primary">
                {owners.length === 1 ? 'Owner' : 'Owners'}:
              </span>{' '}
              {ownerNames}
              <span className="text-theme-text-tertiary"> — only an owner can grant or change the owner role.</span>
            </>
          ) : (
            'This tenant has no owner, and only an owner can grant the owner role. Contact Splashtop support.'
          )}
        </p>
      )}

      {/* Users List */}
      {activeTab === 'users' && (
        <div className="bg-theme-card border border-theme-border-primary rounded-lg overflow-hidden">
          {usersLoading ? (
            <div className="p-6">
              <div className="animate-pulse space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-16 bg-theme-tertiary rounded-lg" />
                ))}
              </div>
            </div>
          ) : users?.length > 0 ? (
            <div className="divide-y divide-theme-border-primary">
              {users.map((user: User) => (
                <div
                  key={user.id}
                  className="px-6 py-4 flex items-center justify-between hover:bg-theme-secondary transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-theme-tertiary rounded-full flex items-center justify-center">
                      <span className="text-theme-text-primary font-medium">
                        {user.full_name?.charAt(0)?.toUpperCase() || user.email.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-theme-text-primary">{user.full_name}</p>
                      <p className="text-sm text-theme-text-tertiary">{user.email}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded ${roleBadgeClass(user.role)}`}
                      >
                        {formatRole(user.role)}
                      </span>
                      {user.role === 'owner' && !actingIsOwner && user.id !== currentUser?.id && (
                        <span className="text-xs text-theme-text-tertiary">Only an owner can change this role</span>
                      )}
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded ${
                          user.sso_provider
                            ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                        }`}
                        title={user.sso_provider ? `Provisioned via ${user.sso_provider}` : 'Local account with password'}
                      >
                        {user.sso_provider ? formatSSOProvider(user.sso_provider) : 'Built-in'}
                      </span>
                      {!user.sso_provider && (
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded ${
                            user.mfa_enabled
                              ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'
                              : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                          }`}
                          title={user.mfa_enabled ? 'Two-factor authentication enrolled' : 'Two-factor authentication not enrolled'}
                        >
                          {user.mfa_enabled ? '2FA on' : '2FA off'}
                        </span>
                      )}
                    </div>

                    <div className="text-right text-sm">
                      <p className="text-theme-text-secondary">
                        {user.last_login ? `Last login: ${formatDate(user.last_login)}` : 'Never logged in'}
                      </p>
                    </div>

                    {user.id !== currentUser?.id && (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => {
                            setSelectedUser(user)
                            setRoleForm(user.role)
                            setError(null)
                            setIsRoleModalOpen(true)
                          }}
                          disabled={user.role === 'owner' && !actingIsOwner}
                          aria-label={
                            user.role === 'owner' && !actingIsOwner
                              ? "Only an owner can change another owner's role"
                              : `Change role of ${user.full_name || user.email}`
                          }
                          className="p-2 text-theme-text-tertiary hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                          title={
                            user.role === 'owner' && !actingIsOwner
                              ? "Only an owner can change another owner's role"
                              : 'Change role'
                          }
                        >
                          <PencilSquareIcon className="w-5 h-5" />
                        </button>
                        {user.mfa_enabled && (
                          <button
                            onClick={() => {
                              setError(null)
                              setMfaResetUser(user)
                            }}
                            className="p-2 text-theme-text-tertiary hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors"
                            title="Reset two-factor authentication"
                          >
                            <ShieldExclamationIcon className="w-5 h-5" />
                          </button>
                        )}
                        <button
                          onClick={() => {
                            setSelectedUser(user)
                            setIsRemoveModalOpen(true)
                          }}
                          className="p-2 text-theme-text-tertiary hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          title="Remove user"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-12 text-center">
              <UserIcon className="w-12 h-12 mx-auto text-theme-text-tertiary mb-4" />
              <p className="text-theme-text-secondary mb-4">No users yet</p>
              <button
                onClick={() => setIsInviteModalOpen(true)}
                className="text-blue-500 hover:text-blue-400"
              >
                Invite your first user
              </button>
            </div>
          )}
        </div>
      )}

      {/* Invitations List */}
      {activeTab === 'invitations' && (
        <div className="bg-theme-card border border-theme-border-primary rounded-lg overflow-hidden">
          {invitationsLoading ? (
            <div className="p-6">
              <div className="animate-pulse space-y-4">
                {[1, 2].map((i) => (
                  <div key={i} className="h-16 bg-theme-tertiary rounded-lg" />
                ))}
              </div>
            </div>
          ) : pendingInvitations.length > 0 ? (
            <div className="divide-y divide-theme-border-primary">
              {pendingInvitations.map((invitation: Invitation) => (
                <div
                  key={invitation.id}
                  className="px-6 py-4 flex items-center justify-between hover:bg-theme-secondary transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center">
                      <EnvelopeIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                    </div>
                    <div>
                      <p className="font-medium text-theme-text-primary">
                        {invitation.email}
                        {invitation.sign_in_method === 'sso' && (
                          <span
                            className="ml-2 px-1.5 py-0.5 text-xs font-medium rounded bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                            title="Accepted by signing in with single sign-on"
                          >
                            SSO
                          </span>
                        )}
                      </p>
                      <p className="text-sm text-theme-text-tertiary">
                        Invited by {invitation.invited_by?.username || 'Unknown'} on {formatDate(invitation.created_at)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded ${roleBadgeClass(invitation.role)}`}
                    >
                      {formatRole(invitation.role)}
                    </span>

                    <div className="flex items-center gap-1 text-sm text-theme-text-tertiary">
                      <ClockIcon className="w-4 h-4" />
                      Expires {formatDate(invitation.expires_at)}
                    </div>

                    {/* Copy invite link */}
                    <button
                      onClick={async () => {
                        if (invitation.invite_link) {
                          try {
                            await navigator.clipboard.writeText(invitation.invite_link)
                          } catch {
                            const ta = document.createElement('textarea')
                            ta.value = invitation.invite_link
                            document.body.appendChild(ta)
                            ta.select()
                            document.execCommand('copy')
                            document.body.removeChild(ta)
                          }
                          setCopiedInvitationId(invitation.id)
                          setTimeout(() => setCopiedInvitationId(null), 2000)
                        }
                      }}
                      disabled={!invitation.invite_link}
                      className={`p-2 rounded-lg transition-colors ${
                        copiedInvitationId === invitation.id
                          ? 'text-green-500 bg-green-50 dark:bg-green-900/20'
                          : 'text-theme-text-tertiary hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                      } disabled:opacity-30 disabled:cursor-not-allowed`}
                      title={copiedInvitationId === invitation.id ? 'Copied!' : 'Copy invite link'}
                    >
                      {copiedInvitationId === invitation.id ? (
                        <ClipboardDocumentCheckIcon className="w-5 h-5" />
                      ) : (
                        <ClipboardDocumentIcon className="w-5 h-5" />
                      )}
                    </button>

                    {/* Resend invitation */}
                    <button
                      onClick={() => {
                        setSelectedInvitation(invitation)
                        resendMutation.mutate({ token: token!, invitationId: invitation.id })
                      }}
                      disabled={resendMutation.isPending && selectedInvitation?.id === invitation.id}
                      className="p-2 text-theme-text-tertiary hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors disabled:opacity-50"
                      title="Resend invitation"
                    >
                      <ArrowPathIcon className={`w-5 h-5 ${resendMutation.isPending && selectedInvitation?.id === invitation.id ? 'animate-spin' : ''}`} />
                    </button>

                    {/* Revoke invitation */}
                    <button
                      onClick={() => {
                        setSelectedInvitation(invitation)
                        revokeMutation.mutate({ token: token!, invitationId: invitation.id })
                      }}
                      disabled={revokeMutation.isPending && selectedInvitation?.id === invitation.id}
                      className="p-2 text-theme-text-tertiary hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
                      title="Revoke invitation"
                    >
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-12 text-center">
              <CheckCircleIcon className="w-12 h-12 mx-auto text-green-500 mb-4" />
              <p className="text-theme-text-secondary">No pending invitations</p>
            </div>
          )}
        </div>
      )}

      {/* Invite User Modal */}
      {isInviteModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black bg-opacity-50"
            onClick={() => {
              setIsInviteModalOpen(false)
              setError(null)
            }}
          />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative w-full max-w-md bg-theme-card rounded-lg shadow-xl border border-theme-border-primary">
              <div className="flex items-center justify-between p-4 border-b border-theme-border-primary">
                <h2 className="text-lg font-semibold text-theme-text-primary">Invite User</h2>
                <button
                  onClick={() => {
                    setIsInviteModalOpen(false)
                    setError(null)
                  }}
                  className="text-theme-text-tertiary hover:text-theme-text-primary"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                {error && (
                  <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg">
                    <p className="text-red-700 dark:text-red-400 text-sm">{error}</p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-theme-text-primary mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                    placeholder="user@example.com"
                    className="w-full px-3 py-2 bg-theme-input border border-theme-border-primary rounded-lg text-theme-text-primary placeholder-theme-text-tertiary focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-theme-text-primary mb-2">
                    Role
                  </label>
                  <select
                    value={inviteForm.role}
                    onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                    className="w-full px-3 py-2 bg-theme-input border border-theme-border-primary rounded-lg text-theme-text-primary focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {roles?.map((role) => (
                      <option key={role.value} value={role.value}>
                        {ROLE_LABELS[role.value] || role.label}
                      </option>
                    ))}
                  </select>
                  {/* What the selected role can do, at the point of choice: the inviter is deciding
                      what this person will be able to do, and a title alone does not say. */}
                  <p className="mt-2 text-sm text-theme-text-tertiary">
                    {ROLE_DESCRIPTIONS[inviteForm.role] ||
                      roles?.find((role) => role.value === inviteForm.role)?.description}
                  </p>
                </div>

                {ssoEnabled && (
                  <div data-testid="sign-in-method">
                    <label className="block text-sm font-medium text-theme-text-primary mb-2">
                      Sign-in method
                    </label>
                    {ssoRequired ? (
                      <p className="text-sm text-theme-text-secondary">
                        {ssoLabel} — your organization requires single sign-on. The invitee accepts by signing in
                        with their work account.
                      </p>
                    ) : (
                      <div className="space-y-2">
                        <label className="flex items-start gap-2 text-sm text-theme-text-primary">
                          <input
                            type="radio"
                            name="sign_in_method"
                            value="sso"
                            checked={inviteForm.sign_in_method === 'sso'}
                            onChange={() => setInviteForm({ ...inviteForm, sign_in_method: 'sso' })}
                            className="mt-0.5"
                          />
                          <span>
                            Single sign-on ({ssoLabel})
                            <span className="block text-theme-text-tertiary">
                              The invitee accepts by signing in with their work account; no password is set.
                            </span>
                          </span>
                        </label>
                        <label className="flex items-start gap-2 text-sm text-theme-text-primary">
                          <input
                            type="radio"
                            name="sign_in_method"
                            value="password"
                            checked={inviteForm.sign_in_method === 'password'}
                            onChange={() => setInviteForm({ ...inviteForm, sign_in_method: 'password' })}
                            className="mt-0.5"
                          />
                          <span>
                            Password
                            <span className="block text-theme-text-tertiary">
                              The invitee sets a password on the invitation page.
                            </span>
                          </span>
                        </label>
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-theme-text-primary mb-2">
                    Personal Message (optional)
                  </label>
                  <textarea
                    value={inviteForm.message}
                    onChange={(e) => setInviteForm({ ...inviteForm, message: e.target.value })}
                    placeholder="Add a personal message to the invitation..."
                    rows={3}
                    className="w-full px-3 py-2 bg-theme-input border border-theme-border-primary rounded-lg text-theme-text-primary placeholder-theme-text-tertiary focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 p-4 border-t border-theme-border-primary">
                <button
                  onClick={() => {
                    setIsInviteModalOpen(false)
                    setError(null)
                  }}
                  className="px-4 py-2 text-theme-text-secondary hover:text-theme-text-primary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleInvite}
                  disabled={inviteMutation.isPending}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-colors"
                >
                  {inviteMutation.isPending ? 'Sending...' : 'Send Invitation'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Change Role Modal */}
      {isRoleModalOpen && selectedUser && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black bg-opacity-50"
            onClick={() => {
              setIsRoleModalOpen(false)
              setError(null)
            }}
          />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative w-full max-w-md bg-theme-card rounded-lg shadow-xl border border-theme-border-primary">
              <div className="flex items-center justify-between p-4 border-b border-theme-border-primary">
                <h2 className="text-lg font-semibold text-theme-text-primary">Change Role</h2>
                <button
                  onClick={() => {
                    setIsRoleModalOpen(false)
                    setError(null)
                  }}
                  className="text-theme-text-tertiary hover:text-theme-text-primary"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                {error && (
                  <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg">
                    <p className="text-red-700 dark:text-red-400 text-sm">{error}</p>
                  </div>
                )}

                <p className="text-theme-text-primary">
                  Choose a new role for <span className="font-semibold">{selectedUser.full_name}</span>.
                </p>

                <div>
                  <label className="block text-sm font-medium text-theme-text-primary mb-2">
                    Role
                  </label>
                  <select
                    value={roleForm}
                    onChange={(e) => setRoleForm(e.target.value)}
                    className="w-full px-3 py-2 bg-theme-input border border-theme-border-primary rounded-lg text-theme-text-primary focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {/* Only an owner hands out the owner role, so the option is not offered to
                        anyone the server would refuse. */}
                    {roles
                      ?.filter((role) => role.value !== 'owner' || actingIsOwner)
                      .map((role) => (
                        <option key={role.value} value={role.value}>
                          {ROLE_LABELS[role.value] || role.label}
                        </option>
                      ))}
                  </select>
                  {!actingIsOwner && (
                    <p className="mt-2 text-sm text-amber-700 dark:text-amber-400" data-testid="owner-role-note">
                      Only an owner can grant or change the owner role.
                      {ownerNames ? ` Ask ${ownerNames}.` : ' Contact Splashtop support.'}
                    </p>
                  )}
                  {/* What the selected role can do, at the point of choice — same reasoning as the
                      invite dialog: a title alone does not say what this person will be able to do. */}
                  <p className="mt-2 text-sm text-theme-text-tertiary">
                    {ROLE_DESCRIPTIONS[roleForm] ||
                      roles?.find((role) => role.value === roleForm)?.description}
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-3 p-4 border-t border-theme-border-primary">
                <button
                  onClick={() => {
                    setIsRoleModalOpen(false)
                    setError(null)
                  }}
                  className="px-4 py-2 text-theme-text-secondary hover:text-theme-text-primary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleChangeRole}
                  disabled={roleMutation.isPending || roleForm === selectedUser.role}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-colors"
                >
                  {roleMutation.isPending ? 'Saving...' : 'Change Role'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Remove User Modal */}
      {mfaResetUser && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black bg-opacity-50"
            onClick={() => setMfaResetUser(null)}
          />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative w-full max-w-md bg-theme-card rounded-lg shadow-xl border border-theme-border-primary">
              <div className="flex items-center justify-between p-4 border-b border-theme-border-primary">
                <h2 className="text-lg font-semibold text-theme-text-primary">Reset two-factor authentication</h2>
                <button
                  onClick={() => setMfaResetUser(null)}
                  className="text-theme-text-tertiary hover:text-theme-text-primary"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
              <div className="p-4 space-y-3 text-sm text-theme-text-secondary">
                <p>
                  This clears the authenticator app and the recovery codes on{' '}
                  <span className="font-medium text-theme-text-primary">{mfaResetUser.email}</span> and signs
                  them out everywhere. If your organization requires two-factor authentication, their next
                  sign-in asks them to set it up again.
                </p>
                <p>Only do this for someone who has lost their phone and their recovery codes.</p>
              </div>
              <div className="flex justify-end gap-3 p-4 border-t border-theme-border-primary">
                <button
                  onClick={() => setMfaResetUser(null)}
                  className="px-4 py-2 bg-theme-tertiary hover:bg-theme-secondary text-theme-text-primary rounded-lg transition-colors"
                  disabled={mfaResetMutation.isPending}
                >
                  Cancel
                </button>
                <button
                  onClick={() => mfaResetMutation.mutate(mfaResetUser.id)}
                  disabled={mfaResetMutation.isPending}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  {mfaResetMutation.isPending ? 'Resetting…' : 'Reset and sign out'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isRemoveModalOpen && selectedUser && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black bg-opacity-50"
            onClick={() => setIsRemoveModalOpen(false)}
          />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative w-full max-w-md bg-theme-card rounded-lg shadow-xl border border-theme-border-primary">
              <div className="flex items-center justify-between p-4 border-b border-theme-border-primary">
                <h2 className="text-lg font-semibold text-theme-text-primary">Remove User</h2>
                <button
                  onClick={() => setIsRemoveModalOpen(false)}
                  className="text-theme-text-tertiary hover:text-theme-text-primary"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6">
                <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 dark:bg-red-900/30 rounded-full">
                  <ExclamationTriangleIcon className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
                <p className="text-center text-theme-text-primary mb-2">
                  Are you sure you want to remove{' '}
                  <span className="font-semibold">{selectedUser.full_name}</span>?
                </p>
                <p className="text-center text-theme-text-secondary text-sm">
                  This user will lose access to this organization immediately.
                </p>
              </div>

              <div className="flex justify-end gap-3 p-4 border-t border-theme-border-primary">
                <button
                  onClick={() => setIsRemoveModalOpen(false)}
                  className="px-4 py-2 text-theme-text-secondary hover:text-theme-text-primary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRemoveUser}
                  disabled={removeMutation.isPending}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-lg transition-colors"
                >
                  {removeMutation.isPending ? 'Removing...' : 'Remove User'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invite Success Modal with Link */}
      {inviteSuccessModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black bg-opacity-50"
            onClick={() => setInviteSuccessModal(null)}
          />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative w-full max-w-lg bg-theme-card rounded-lg shadow-xl border border-theme-border-primary">
              <div className="flex items-center justify-between p-4 border-b border-theme-border-primary">
                <h2 className="text-lg font-semibold text-theme-text-primary">Invitation Sent</h2>
                <button
                  onClick={() => setInviteSuccessModal(null)}
                  className="text-theme-text-tertiary hover:text-theme-text-primary"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-green-100 dark:bg-green-900/30 rounded-full">
                  <CheckCircleIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
                <p className="text-center text-theme-text-primary">
                  Invitation sent to <span className="font-semibold">{inviteSuccessModal.email}</span>
                </p>
                <div className="bg-theme-secondary rounded-lg p-4">
                  <p className="text-sm text-theme-text-secondary mb-2">
                    Share this link with the user to complete their registration:
                  </p>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      readOnly
                      value={inviteSuccessModal.link}
                      className="flex-1 px-3 py-2 bg-theme-input border border-theme-border-primary rounded-lg text-theme-text-primary text-sm font-mono"
                    />
                    <button
                      onClick={handleCopyInviteLink}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                        copiedLink
                          ? 'bg-green-600 text-white'
                          : 'bg-blue-600 hover:bg-blue-700 text-white'
                      }`}
                    >
                      {copiedLink ? (
                        <>
                          <ClipboardDocumentCheckIcon className="w-5 h-5" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <ClipboardDocumentIcon className="w-5 h-5" />
                          Copy
                        </>
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-theme-text-tertiary mt-2">
                    This link will expire in 7 days.
                  </p>
                </div>
              </div>

              <div className="flex justify-end p-4 border-t border-theme-border-primary">
                <button
                  onClick={() => setInviteSuccessModal(null)}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  )
}

export default UsersSettings
